// types
import type {
    AnyAdjustValues,
    OptionTypes,
    RequestRollToPaymentsPayload,
    RollToActionValues,
    StateTree
} from '@makemydeal/dr-dash-types';
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { IPenciledInputs } from '@makemydeal/dr-platform-types';
import { ApplicationCodes, CASH } from '@makemydeal/dr-platform-types';
import { TRACE_ID_HEADER } from '@makemydeal/dr-shared-bff-types';
import type { RSAASuccessType } from 'redux-api-middleware';
import type { RequestRollToOptionsPayload, RequestRollToPaymentUPQPayload } from '../reducers/rollToOptionsReducer';
import { v4 as uuid } from 'uuid';

// libraries
import { omit } from 'lodash';
import { RSAA } from 'redux-api-middleware';

// selectors
import * as offerReduxSelectors from '../selectors/offerRedux';
import * as rollToSelectors from '../selectors/rollToSelectors';

// utils
import { getPaymentUrl, getUserProgramRollsUrl } from './paymentServiceUtils';

// utils
import { paymentPayloadUtils, utils } from '@makemydeal/dr-offer-redux';

// constants
import { ROLL_TO_OPTIONS_LABELS } from '@makemydeal/dr-shared-types';
import { ROLL_TO_OPTION_BY_TAB, ROLL_TO_TARGET_FIELD_MAP } from '../constants';
import {
    CLEAR_ROLL_TO_ERRORS,
    REQUEST_ROLL_TO_PAYMENTS,
    REQUEST_ROLL_TO_PAYMENTS_FAILURE,
    REQUEST_ROLL_TO_PAYMENTS_SUCCESS_UPQ,
    REQUEST_ROLL_TO_PAYMENTS_SUCCESS_UPR,
    UPDATE_ROLL_TO_OPTIONS
} from '../actionTypes/rollToOptionsActionTypes';
import { buildPaymentPayloadHostSelectors } from './offerPayloadUtil';
import { ensureUserProgramQuotesForRollTo, getRollToSession } from './rollToEndpointUtils';

const getUprSuccessActionType = ({
    activeTab,
    targetValue
}: {
    activeTab: ROLL_TO_OPTIONS_LABELS;
    targetValue: number;
}): RSAASuccessType<StateTree, RequestRollToPaymentsPayload | undefined> => ({
    type: REQUEST_ROLL_TO_PAYMENTS_SUCCESS_UPR,
    payload: async (_action, _state, res) => {
        const data = await res.json();
        return {
            ...data,
            [ROLL_TO_TARGET_FIELD_MAP[activeTab]]: targetValue
        };
    }
});

const getUpqSuccessActionType = ({
    currentOfferType,
    activeTab
}: {
    currentOfferType: string;
    activeTab: ROLL_TO_OPTIONS_LABELS;
}): RSAASuccessType<StateTree, RequestRollToPaymentUPQPayload | undefined> => ({
    type: REQUEST_ROLL_TO_PAYMENTS_SUCCESS_UPQ,
    payload: async (_action, state, res) => {
        const data = await res.json();
        const VppAndProtectionTotal = rollToSelectors.getVppAndProtectionTotal(state);
        // Every access below is on `quote`, so both shapes fall back to {} rather than throwing on a
        // response that omits the offer type node or its payments array.
        const quote = currentOfferType !== CASH ? data[currentOfferType]?.payments?.at(0) || {} : data[currentOfferType] || {};
        const balanceDue = currentOfferType !== CASH ? quote.amountFinanced : quote.totalAmountDueAfterCredits;
        // One Pay Lease rolls to cash due from customer rather than amount financed (US2013180). Both amounts are
        // always reported so no consumer can read a value left over from a previously active tab.
        const cashDue = quote.cashDueFromCustomer;
        const actionValueByTab: Record<ROLL_TO_OPTIONS_LABELS, number | undefined> = {
            [ROLL_TO_OPTIONS_LABELS.PAYMENT]: quote.monthlyPayment,
            [ROLL_TO_OPTIONS_LABELS.BALANCE_DUE]: balanceDue,
            [ROLL_TO_OPTIONS_LABELS.CASH_DUE]: cashDue,
            [ROLL_TO_OPTIONS_LABELS.PROFIT]: quote.profit?.frontEndProfit,
            [ROLL_TO_OPTIONS_LABELS.FRONT_PROFIT]: quote.profit?.frontEndProfit
        };
        const payloadValues: RequestRollToPaymentUPQPayload = {
            profit: quote.profit,
            actionValue: actionValueByTab[activeTab],
            totalTaxes: quote.totalTax,
            balanceDue,
            cashDue,
            totalFees: quote.totalFee,
            VppAndProtectionTotal,
            payment: quote.monthlyPayment
        };

        return payloadValues;
    }
});

const getIsOptionToUpdateAdjust = ({ optionType }: { optionType: OptionTypes }) => {
    return { isOptionToUpdateAdjust: optionType === 'adjust' };
};

export const handleRollToPayment = async (params: { state: StateTree; action: AnyFSA; store: any; optionType: OptionTypes }) => {
    const { action, store, optionType } = params;
    const session = getRollToSession(params.state);
    const { switched, settled } = ensureUserProgramQuotesForRollTo({
        dispatch: store.dispatch,
        getState: () => params.state
    });
    if (settled) {
        store.dispatch({ type: REQUEST_ROLL_TO_PAYMENTS });
        await settled;
        if (getRollToSession(store.getState()) !== session) return;
    }
    const state: StateTree = switched ? store.getState() : params.state;

    // Clear any existing errors before making new requests
    store.dispatch({ type: CLEAR_ROLL_TO_ERRORS });

    const activeTab = rollToSelectors.getActiveTab(state) || ROLL_TO_OPTIONS_LABELS.PAYMENT;
    const rollToOptionsItems = rollToSelectors.getRollToOptionsItems(state);
    const currentValues = rollToOptionsItems.current;
    const adjustedValues = rollToSelectors.getAdjustedValues(state);
    // Only use action.payload if it's a valid RollToActionValues object (not a primitive like a string)
    // When switching tabs (payload is a string like "Profit"), preserve existing adjustedValues
    const isValidPayloadObject = action.payload && typeof action.payload === 'object' && !Array.isArray(action.payload);
    const payloadValues: AnyAdjustValues =
        isValidPayloadObject && optionType === 'adjust' ? action.payload : adjustedValues || currentValues;
    const offerDetails = offerReduxSelectors.getPenciledInputs(state);
    const targetValueByTab: Record<ROLL_TO_OPTIONS_LABELS, number> = {
        [ROLL_TO_OPTIONS_LABELS.PAYMENT]: rollToSelectors.getRollTargetPayment(state),
        [ROLL_TO_OPTIONS_LABELS.BALANCE_DUE]: rollToSelectors.getTargetBalanceDue(state),
        [ROLL_TO_OPTIONS_LABELS.CASH_DUE]: rollToSelectors.getTargetCashDue(state),
        [ROLL_TO_OPTIONS_LABELS.PROFIT]: rollToSelectors.getRollTargetProfit(state),
        [ROLL_TO_OPTIONS_LABELS.FRONT_PROFIT]: rollToSelectors.getRollTargetProfit(state)
    };
    const targetValue = targetValueByTab[activeTab];
    const adjustedOptions = rollToSelectors.getAdjustedStatusOptions(state, payloadValues);
    const currentOfferType = offerReduxSelectors.getCurrentOfferType(state);

    const penciledInputs: IPenciledInputs = utils.buildRollToPenciledInputsState(offerDetails, adjustedOptions, currentOfferType);
    const rollPaymentPayload = {
        [ROLL_TO_OPTION_BY_TAB[activeTab]]: targetValue
    };

    const rollState = {
        ...state,
        offer: {
            ...state.offer,
            penciledInputs: {
                ...state.offer?.penciledInputs,
                ...penciledInputs
            }
        }
    };

    //host selectors might have some other options which not covered locally to construct a proper payload!!!
    const hostSelectors = buildPaymentPayloadHostSelectors();

    const payload = paymentPayloadUtils.createPaymentPayload(rollState, hostSelectors, {
        type: REQUEST_ROLL_TO_PAYMENTS,
        meta: {
            isRollToCall: true,
            sourceApplication: ApplicationCodes.MV,
            paymentRequestOverride: {
                [currentOfferType]: {
                    termMonths: adjustedOptions.term.value
                }
            }
        }
    });

    const uprEndpoint = getUserProgramRollsUrl(state);

    const upqEndpoint = getPaymentUrl(state);

    const uprSuccessActionType = getUprSuccessActionType({ activeTab, targetValue });

    const upqSuccessActionType = getUpqSuccessActionType({ currentOfferType, activeTab });

    const rsaCall = (
        endpoint: string,
        successActionType: RSAASuccessType,
        rollToPayload: Record<string, unknown> & { traceId: string }
    ) => ({
        [RSAA]: {
            endpoint: endpoint,
            method: 'POST',
            body: JSON.stringify(rollToPayload),
            headers: { 'Content-Type': 'application/json', [TRACE_ID_HEADER]: rollToPayload.traceId },
            types: [REQUEST_ROLL_TO_PAYMENTS, successActionType, REQUEST_ROLL_TO_PAYMENTS_FAILURE]
        }
    });

    await Promise.all([
        store.dispatch(
            rsaCall(uprEndpoint, uprSuccessActionType, {
                ...payload,
                payment: {
                    ...payload.payment,
                    ...rollPaymentPayload
                },
                traceId: uuid()
            })
        ),
        store.dispatch(rsaCall(upqEndpoint, upqSuccessActionType, { ...payload, traceId: uuid() } as any))
    ]);

    const newAdjustValues: RollToActionValues = omit(
        {
            ...adjustedValues,
            ...payloadValues
        },
        ['actionValue']
    );

    const { isOptionToUpdateAdjust } = getIsOptionToUpdateAdjust({ optionType });

    const requestRollToOptionsPayload: RequestRollToOptionsPayload = {
        rollToItems: {
            adjust: isOptionToUpdateAdjust ? newAdjustValues : currentValues
        }
    };

    store.dispatch({
        type: UPDATE_ROLL_TO_OPTIONS,
        payload: requestRollToOptionsPayload
    });
};
